import React, { PropsWithChildren, Dispatch, SetStateAction, useEffect } from 'react';
import {
  FloatingOverlay,
  FloatingPortal,
  FloatingFocusManager,
  FloatingNode,
  useInteractions,
  useFloating,
  useRole,
  useDismiss,
  useFloatingTree,
  useFloatingNodeId,
  OpenChangeReason,
  FloatingTreeType,
  ReferenceType,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useManualDismiss } from './useManualDismiss';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { FLOATING_ROOT_ID, FLOATING_ORDER } from '../../constants';
import { getZIndex, CSSValueWithLength, buildCSSWithLength } from '../../styles';
import { semantic_colors, shapes, BorderRadiusSize } from '../../foundation';

export type ModalBaseCancelReason = 'cancelClick' | 'escapeKeyPress' | 'overlayClick';
export type ModalCloseEventCallback = Omit<ModalBaseCancelReason, 'cancelClick'>;

export const SAFE_TOP_BOTTOM_AREA = 120;
export const SAFE_LEFT_RIGHT_AREA = 32;

export interface ModalOverlayBaseProps {
  /** @default false */
  fill?: boolean;
  /**
   * Modal 너비를 지정할 수 있습니다.
   * @default 480
   */
  width?: CSSValueWithLength;
  className?: string;
  open?: boolean;
  /** @default getZIndex('modal') */
  zIndex?: number;
  /**
   * outside 영역 클릭시 닫힘 사용 여부
   * @default true
   */
  shouldCloseOnOutsideClick?: boolean;
  /**
   * escapeKey 키이벤트 닫힘 사용 여부
   * @default true
   */
  shouldCloseOnEscapeKey?: boolean;
  /**
   * reference 요소로 포커스 반환 여부
   * @default true
   */
  returnFocus?: boolean;
  /**
   * Modal destroy시 전달합니다.
   */
  onExited?: () => void;
  onClose?: () => void;
}

export interface ModalOverlayProps extends ModalOverlayBaseProps {
  /**
   * FloatingPortal node id, render 함수로 Floating 노드를 생성하는 경우가 아닌 경우에는 디폴트 아이디값으로 처리합니다.
   * @default FLOATING_ROOT_ID
   */
  floatingPortalId?: string;
  /** @default false */
  fullScreen?: boolean;
  /** @default 'large' */
  borderRadiusSize?: BorderRadiusSize;
  setOpened?: Dispatch<SetStateAction<boolean>>;
  onCancelEventCallback?: (event: ModalCloseEventCallback, floatingContainerId?: string) => void;
  /**
   * @desc esc, overlay 클릭으로 인한 모달 닫힘 상태 외부 제어 여부
   * @default false
   */
  controlledOpen?: boolean;
  /**
   * autofocus 지정 콘텐츠 요소 여부, true일 경우 FloatingFocusManager initialFocus는 자체 포커스 관리 충돌 방지를 위해 -1, false일 경우 기본값 0으로 처리됩니다.
   * @default false
   */
  autoFocus?: boolean;
}

const MotionFloatingOverlay = motion(FloatingOverlay);

export const ModalOverlay = ({
  floatingPortalId = FLOATING_ROOT_ID,
  width,
  borderRadiusSize = 'large',
  fill = false,
  fullScreen = false,
  className,
  children,
  open: openProp,
  zIndex = getZIndex('modal'),
  shouldCloseOnOutsideClick = true,
  shouldCloseOnEscapeKey = true,
  controlledOpen = false,
  autoFocus = false,
  returnFocus = true,
  onExited,
  onClose,
  onCancelEventCallback,
  setOpened,
}: PropsWithChildren<ModalOverlayProps>) => {
  const nodeId = useFloatingNodeId();
  const tree = useFloatingTree();
  const floatingOverlayId = `${FLOATING_ROOT_ID}-overlay-${nodeId}`;

  const handleOpen = (open: boolean) => {
    // dismiss 제어권이 외부에 있는 경우(controlledOpen) 내부 상태 닫힘 처리하지 않습니다.
    if (controlledOpen) return;
    setOpened?.(open);
  };

  const { refs, context } = useFloating({
    open: openProp,
    onOpenChange: handleOpen,
    nodeId,
  });

  const { getFloatingProps } = useInteractions([
    useRole(context, { role: 'dialog' }),
    useDismiss(context, {
      escapeKey: shouldCloseOnEscapeKey,
      outsidePress: shouldCloseOnOutsideClick,
      outsidePressEvent: 'mousedown',
      bubbles: false,
    }),
  ]);

  useEffect(() => {
    if (!openProp) {
      onClose?.();
    }
  }, [openProp]);

  const handleManualDismiss = (reason: OpenChangeReason) => {
    const convertCancelEvent = {
      ['outside-press']: 'overlayClick',
      ['escape-key']: 'escapeKeyPress',
      'ancestor-scroll': undefined,
      'click': undefined,
      'focus': undefined,
      'hover': undefined,
      'list-navigation': undefined,
      'reference-press': undefined,
      'safe-polygon': undefined,
    }[reason];

    convertCancelEvent && onCancelEventCallback?.(convertCancelEvent);
  };

  useManualDismiss({
    controlled: controlledOpen,
    rootId: floatingPortalId,
    tree: tree as FloatingTreeType<ReferenceType>,
    context,
    callbackTypeWithFn: handleManualDismiss,
  });

  return (
    <FloatingNode id={nodeId}>
      <AnimatePresence onExitComplete={onExited}>
        {openProp && (
          <FloatingPortal id={floatingPortalId}>
            <MotionFloatingOverlay
              id={floatingOverlayId}
              lockScroll
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'grid', placeItems: 'center', background: semantic_colors.background.scrim, zIndex }}
            >
              <FloatingFocusManager
                context={context}
                initialFocus={autoFocus ? -1 : 0}
                returnFocus={returnFocus}
                order={FLOATING_ORDER}
              >
                <ModalContainer
                  {...getFloatingProps({
                    className: classNames('pds-modal', className),
                    ref: refs.setFloating,
                    onClick: (e: React.MouseEvent) => e.stopPropagation(),
                  })}
                  initial={{ y: fullScreen ? '40%' : -25 }}
                  animate={{ y: 0 }}
                  exit={{ y: fullScreen ? '80%' : -50 }}
                  transition={{ ease: 'easeIn', duration: fullScreen ? 0.22 : 0.15 }}
                  width={width}
                  fullScreen={fullScreen}
                  $fill={fill}
                  aria-modal
                >
                  <ModalWrapper
                    className={classNames('pds-modal-wrapper', className)}
                    borderRadiusSize={borderRadiusSize}
                    fullScreen={fullScreen}
                  >
                    {children}
                  </ModalWrapper>
                </ModalContainer>
              </FloatingFocusManager>
            </MotionFloatingOverlay>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </FloatingNode>
  );
};

const ModalContainer = styled(motion.div)<{
  width?: CSSValueWithLength;
  fullScreen: Boolean;
  $fill: Boolean;
}>`
  ${({ width, fullScreen, $fill }) =>
    $fill || fullScreen ? `width: 100%` : width ? buildCSSWithLength('width', width) : 'width: min-content'};
  ${({ fullScreen }) =>
    fullScreen
      ? `
        margin: 0;
        height: 100%;
        position: fixed;
        top: 0;
        left: 0;
      `
      : `
        max-height: calc(100vh - ${SAFE_TOP_BOTTOM_AREA}px);
        max-width: calc(100vw - ${SAFE_LEFT_RIGHT_AREA}px);
    `};

  &:focus {
    outline: none;
  }
`;

const ModalWrapper = styled.div<{ borderRadiusSize: BorderRadiusSize; fullScreen: Boolean }>`
  background-color: ${semantic_colors.background.surface};
  margin: 0 auto;
  word-wrap: break-word;
  ${shapes.shadow.medium};
  ${({ borderRadiusSize, fullScreen }) => !fullScreen && shapes.border_radius[borderRadiusSize]}
  ${({ fullScreen }) => fullScreen && 'height: 100%; display: flex; flex-direction: column;'}
`;
